import { quotes as api } from "@/lib/api/quotes";
import { useLiveQuotesStore } from "@/stores/liveQuotesStore";

export interface QuoteRow {
  id: number;
  ticker: string;
  price: number;
  timestamp: string;
  source: string;
}

export function appendQuotes(ticker: string, rows: QuoteRow[]): void {
  if (rows.length === 0) return;
  useLiveQuotesStore.getState().appendRowsForTicker(ticker, rows);
}

const seeded = new Set<string>();
const inFlight = new Map<string, Promise<void>>();

function dedup(key: string, task: () => Promise<void>): Promise<void> {
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = task().finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
}

export function seedLatestQuote(ticker: string): Promise<void> {
  const key = ticker.toUpperCase();
  if (seeded.has(`latest:${key}`)) return Promise.resolve();
  return dedup(`latest:${key}`, async () => {
    const rows = await api
      .listQuotes({ tickers: [key], summary: true })
      .catch(() => null);
    if (rows === null) return;
    seeded.add(`latest:${key}`);
    const own = rows.filter((r) => r.ticker.toUpperCase() === key);
    if (own.length > 0) appendQuotes(key, own);
  });
}

export function seedIntradayQuotes(
  ticker: string,
  from?: string,
): Promise<void> {
  const key = ticker.toUpperCase();
  const tag = from ?? "";
  const cacheKey = `intraday:${key}:${tag}`;
  if (seeded.has(cacheKey)) return Promise.resolve();
  return dedup(cacheKey, async () => {
    const rows = await api
      .listQuotes({ tickers: [key], summary: false, from })
      .catch(() => null);
    if (rows === null) return;
    seeded.add(cacheKey);
    const own = rows.filter((r) => r.ticker.toUpperCase() === key);
    if (own.length > 0) appendQuotes(key, own);
  });
}

export function __resetQuoteSeedForTests(): void {
  seeded.clear();
  inFlight.clear();
}
